import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../../lib/api";
import { useUnsavedGuard } from "./unsavedGuard";
import { SavedNote, CheckIcon } from "../shared/Icons";

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  color: "var(--ink-secondary)",
  display: "block",
  marginBottom: 6,
};

const inputStyle: React.CSSProperties = {
  padding: "8px 12px",
  background: "var(--surface-input)",
  border: "1px solid var(--line)",
  borderRadius: 8,
  color: "var(--ink)",
  fontSize: 13,
  outline: "none",
  width: "100%",
  boxSizing: "border-box" as const,
};

const primaryBtn: React.CSSProperties = {
  background: "var(--brand)", color: "#fff", border: "none", borderRadius: 8,
  padding: "8px 20px", fontSize: 13, cursor: "pointer", alignSelf: "flex-start",
};

const ghostBtn: React.CSSProperties = {
  background: "var(--surface-hover)", color: "var(--ink-secondary)", border: "1px solid var(--line-strong)",
  borderRadius: 8, padding: "6px 12px", fontSize: 13, cursor: "pointer",
};

type OrgRow = { origOrgId: string; org_id: string; token: string; hadToken: boolean };
type Dist = { org_id: string; target_type: "team" | "user"; target_id: string; target_name: string };
type Target = { id: string; name: string };
type OrgName = { org_id: string; name: string };

function orgPayload(o: OrgRow) {
  const sameOrg = o.origOrgId !== "" && o.org_id.trim() === o.origOrgId;
  const t = o.token.trim();
  // token novo → cru (API cifra). Vazio → "***" preserva só se a org não mudou; senão "".
  return { org_id: o.org_id.trim(), token_encrypted: t ? t : o.hadToken && sameOrg ? "***" : "" };
}

export function ConfigCRMillion({ agent }: { agent: any }) {
  const qc = useQueryClient();
  const c = agent.crmillion ?? {};

  const [enabled, setEnabled] = useState<boolean>(c.enabled ?? false);
  const [orgs, setOrgs] = useState<OrgRow[]>(
    (c.orgs ?? []).map((o: any) => ({
      origOrgId: o.org_id ?? "", org_id: o.org_id ?? "", token: "", hadToken: o.token_encrypted === "***",
    })),
  );
  const [dist, setDist] = useState<Dist>({
    org_id: c.distribution?.org_id ?? "",
    target_type: c.distribution?.target_type ?? "team",
    target_id: c.distribution?.target_id ?? "",
    target_name: c.distribution?.target_name ?? "",
  });
  const [tab, setTab] = useState<"orgs" | "dist">("orgs");
  const [editingIdx, setEditingIdx] = useState<number | null>(null);

  const payload = () => ({ enabled, orgs: orgs.map(orgPayload), distribution: dist });
  const current = JSON.stringify(payload());
  const [savedSnap, setSavedSnap] = useState(current);

  const mut = useMutation({
    mutationFn: () => api.patch(`/agents/${agent.id}`, { crmillion: payload() }),
    onSuccess: () => {
      setSavedSnap(JSON.stringify(payload()));
      qc.invalidateQueries({ queryKey: ["agent", agent.id] });
      qc.invalidateQueries({ queryKey: ["crmillion-orgs", agent.id] });
    },
  });
  useUnsavedGuard(current !== savedSnap, mut.mutateAsync);

  // Nomes das orgs salvas (ao vivo, via API CRMillion) — p/ badges e rótulo da Distribuição.
  const savedOrgIds: string[] = (c.orgs ?? []).map((o: any) => o.org_id).filter(Boolean);
  const { data: orgNames = [] } = useQuery<OrgName[]>({
    queryKey: ["crmillion-orgs", agent.id, savedOrgIds.join(",")],
    queryFn: () => api.get(`/agents/${agent.id}/crmillion/orgs`),
    enabled: savedOrgIds.length > 0,
  });
  const nameOf = (oid: string) => orgNames.find((o) => o.org_id === oid)?.name || oid;

  // ── orgs ──────────────────────────────────────────────────────────────────
  const addOrg = () => {
    setOrgs([...orgs, { origOrgId: "", org_id: "", token: "", hadToken: false }]);
    setEditingIdx(orgs.length);
  };
  const setOrgField = (i: number, k: "org_id" | "token", v: string) =>
    setOrgs(orgs.map((o, j) => (j === i ? { ...o, [k]: v } : o)));
  const delOrg = (i: number) => {
    const removed = orgs[i];
    setOrgs(orgs.filter((_, j) => j !== i));
    setEditingIdx(null);
    if (dist.org_id === removed.org_id) setDist({ ...dist, org_id: "", target_id: "", target_name: "" });
  };

  // ── distribuição ──────────────────────────────────────────────────────────
  const distOrgId = dist.org_id || (savedOrgIds.length === 1 ? savedOrgIds[0] : "");

  const { data: targets = [], isLoading: loadingTargets, isError: targetsError } = useQuery<Target[]>({
    queryKey: ["crmillion-targets", agent.id, dist.target_type, distOrgId],
    queryFn: () =>
      api.get(
        `/agents/${agent.id}/crmillion/${dist.target_type === "team" ? "teams" : "persons"}?org_id=${encodeURIComponent(distOrgId)}`,
      ),
    enabled: tab === "dist" && !!distOrgId,
  });

  const setType = (tt: "team" | "user") =>
    setDist({ org_id: distOrgId, target_type: tt, target_id: "", target_name: "" });
  const setOrg = (oid: string) => setDist({ org_id: oid, target_type: dist.target_type, target_id: "", target_name: "" });
  const pickTarget = (id: string) =>
    setDist({ org_id: distOrgId, target_type: dist.target_type, target_id: id, target_name: targets.find((t) => t.id === id)?.name ?? "" });

  return (
    <div style={{ maxWidth: 520, display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Toggle global — desligar não apaga nada, só corta o envio no fim do fluxo */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <input
          type="checkbox"
          id="crmillion-enabled"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          style={{ accentColor: "var(--brand)", width: 16, height: 16 }}
        />
        <label htmlFor="crmillion-enabled" style={{ fontSize: 13, color: "var(--ink)", cursor: "pointer" }}>
          Habilitar integração CRMillion
        </label>
      </div>

      {/* Sub-abas */}
      <div style={{ display: "flex", gap: 8 }}>
        {([["orgs", "Organizações"], ["dist", "Distribuição"]] as const).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            style={{
              fontSize: 13, padding: "6px 12px", borderRadius: 8, cursor: "pointer",
              border: tab === id ? "none" : "1px solid var(--line-strong)",
              background: tab === id ? "var(--brand)" : "var(--surface-hover)",
              color: tab === id ? "#fff" : "var(--ink-secondary)",
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "orgs" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {orgs.length === 0 && (
            <span style={{ fontSize: 12, color: "var(--ink-soft)" }}>Nenhuma organização cadastrada.</span>
          )}
          {/* Badges (clique p/ editar) */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {orgs.map((o, i) => {
              const isEditing = i === editingIdx;
              const label = o.org_id ? nameOf(o.org_id) : "Nova organização";
              return (
                <button
                  key={i}
                  onClick={() => setEditingIdx(isEditing ? null : i)}
                  title="Editar Org ID e token"
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 7,
                    padding: "6px 12px", borderRadius: 999, fontSize: 13, cursor: "pointer",
                    background: isEditing ? "var(--brand)" : "var(--surface-input)",
                    border: `1px solid ${isEditing ? "var(--brand)" : "var(--line-strong)"}`,
                    color: "var(--ink)",
                  }}
                >
                  <span
                    style={{
                      width: 7, height: 7, borderRadius: "50%",
                      background: o.hadToken || o.token.trim() ? "var(--ok)" : "var(--ink-soft)",
                    }}
                  />
                  {label}
                </button>
              );
            })}
            <button onClick={addOrg} style={{ ...ghostBtn, borderRadius: 999 }}>
              + Adicionar
            </button>
          </div>

          {/* Painel de edição da org selecionada */}
          {editingIdx !== null && orgs[editingIdx] && (() => {
            const o = orgs[editingIdx];
            const renamed = o.origOrgId !== "" && o.org_id.trim() !== o.origOrgId;
            const keepToken = o.hadToken && !renamed;
            return (
              <div style={{ border: "1px solid var(--line)", borderRadius: 8, padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <label style={labelStyle}>Org ID</label>
                  <input
                    style={inputStyle}
                    value={o.org_id}
                    onChange={(e) => setOrgField(editingIdx, "org_id", e.target.value)}
                    placeholder="ex: org_abc123"
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <label style={labelStyle}>
                    Token{keepToken && <span style={{ color: "var(--ok)", marginLeft: 8, display: "inline-flex", alignItems: "center", gap: 4 }}><CheckIcon size={12} /> configurado</span>}
                  </label>
                  <input
                    type="password"
                    style={inputStyle}
                    value={o.token}
                    onChange={(e) => setOrgField(editingIdx, "token", e.target.value)}
                    placeholder={
                      renamed ? "Org ID alterado — informe o token de novo"
                        : o.hadToken ? "deixe em branco para manter o atual"
                        : "cole o token"
                    }
                  />
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => setEditingIdx(null)} style={ghostBtn}>Fechar</button>
                  <button onClick={() => delOrg(editingIdx)} style={{ ...ghostBtn, color: "var(--danger)" }}>Excluir</button>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {tab === "dist" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {savedOrgIds.length === 0 ? (
            <span style={{ fontSize: 12, color: "var(--ink-soft)" }}>
              Cadastre e salve uma organização na aba Organizações antes de distribuir.
            </span>
          ) : (
            <>
              {savedOrgIds.length > 1 && (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <label style={labelStyle}>Organização</label>
                  <select style={inputStyle} value={distOrgId} onChange={(e) => setOrg(e.target.value)}>
                    <option value="">Selecione…</option>
                    {savedOrgIds.map((oid) => (
                      <option key={oid} value={oid}>{nameOf(oid)}</option>
                    ))}
                  </select>
                </div>
              )}

              <div style={{ display: "flex", flexDirection: "column" }}>
                <label style={labelStyle}>Distribuir novos leads para</label>
                <div style={{ display: "flex", gap: 8 }}>
                  {([["team", "Equipe"], ["user", "Pessoa"]] as const).map(([tt, lbl]) => (
                    <button
                      key={tt}
                      onClick={() => setType(tt)}
                      style={{
                        fontSize: 13, padding: "6px 14px", borderRadius: 8, cursor: "pointer",
                        border: dist.target_type === tt ? "none" : "1px solid var(--line-strong)",
                        background: dist.target_type === tt ? "var(--brand)" : "var(--surface-hover)",
                        color: dist.target_type === tt ? "#fff" : "var(--ink-secondary)",
                      }}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>

              {/* Rótulo "Nome da org - Equipe/Pessoa" */}
              {distOrgId && (
                <div style={{ fontSize: 13, color: "var(--ink)" }}>
                  {nameOf(distOrgId)} - {dist.target_type === "team" ? "Equipe" : "Pessoa"}
                  {dist.target_name && <span style={{ color: "var(--ink-mid)" }}> · {dist.target_name}</span>}
                </div>
              )}

              <div style={{ display: "flex", flexDirection: "column" }}>
                <label style={labelStyle}>{dist.target_type === "team" ? "Equipe" : "Pessoa"}</label>
                {targetsError ? (
                  <span style={{ fontSize: 12, color: "var(--danger)" }}>
                    Não consegui listar — confira o token e salve a organização.
                  </span>
                ) : (
                  <select
                    style={inputStyle}
                    value={dist.target_id}
                    onChange={(e) => pickTarget(e.target.value)}
                    disabled={loadingTargets || !distOrgId}
                  >
                    <option value="">{loadingTargets ? "Carregando…" : "Selecione…"}</option>
                    {/* alvo salvo pode não estar na lista durante o load — mantém visível */}
                    {dist.target_id && !targets.some((t) => t.id === dist.target_id) && (
                      <option value={dist.target_id}>{dist.target_name || dist.target_id}</option>
                    )}
                    {targets.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                )}
              </div>
            </>
          )}
        </div>
      )}

      <button onClick={() => mut.mutate()} disabled={mut.isPending} style={primaryBtn}>
        {mut.isPending ? "Salvando..." : "Salvar"}
      </button>
      <SavedNote show={mut.isSuccess} />
    </div>
  );
}
